import { PlusCircle, Trash2 } from "lucide-react";
import { FC, useEffect, useState } from "react";
import Modal from "react-modal";
import { Address } from "../../Store/interfaces";
import { useScheinContext } from "../../Store/schein-context";
import { useUIContext } from "../../Store/ui-context";
// import { setFlagsFromString } from "v8";
import { Colors } from "../../Styles/colors";
import {
  deleteStoredAddress,
  getStoredAddresses,
  postAdresse,
  updateAdresse,
} from "../../Utils/api";
// import { useFetcher } from "react-router-dom";
import CustomButton from "../Ui/CustomButton";

interface StandortModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const dummyAddress = {
  id: 0,
  adresse: "",
  is_default: false,
};

const StandortModal: FC<StandortModalProps> = ({ isVisible, onClose }) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [baseOptions, setBaseOptions] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address>(dummyAddress);
  const [defaultAddress, setDefaultAddress] = useState<Address | undefined>(
    undefined
  );
  const { setStartOrt, startOrt } = useScheinContext();
  const { showDialog } = useUIContext();
  useEffect(() => {
    getAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAddresses = async () => {
    setBaseOptions([]);
    const data = await getStoredAddresses(); // hier als Address
    data.forEach((ad: Address) => {
      if (ad.is_default) {
        setDefaultAddress(ad);
        if (!startOrt) {
          setStartOrt(ad.adresse);
        }
      }
    });
    data ? setBaseOptions(data) : setBaseOptions([]);
  };

  const handleAddBase = async () => {
    if (inputValue.trim() !== "") {
      await postAdresse(inputValue);
      getAddresses();
      setInputValue("");
    }
  };

  const handleRemoveItem = async (data: Address) => {
    showDialog({
      title: "Bestätigung",
      message: `Möchten Sie den Startort ${data.adresse} wirklich löschen?`,
      confirmButtonText: "Löschen",
      cancelButtonText: "Abbrechen",
      onConfirm: async () => {
        await deleteStoredAddress(data.id);
        getAddresses();
      },
      onCancel: () => {},
    });
  };

  const handleRowSelection = (index: number) => {
    setSelectedAddress(baseOptions[index]);
    setStartOrt(baseOptions[index].adresse);
  };

  const handleSetDefaultAddress = async (adresse: Address) => {
    baseOptions.forEach(async (element) => {
      if (element.is_default) {
        element.is_default = !element.is_default;
        await updateAdresse(element);
      }
    });

    adresse.is_default = !adresse.is_default;
    await updateAdresse(adresse);
    setSelectedAddress(adresse);
    getAddresses();
  };

  return (
    <Modal
      contentLabel="Ab- und Rückfahrtsstandort bestimmen"
      isOpen={isVisible}
      onRequestClose={onClose}
      style={{ overlay: { zIndex: 10 } }} // Modal bleibt oben
    >
      <div className="flex flex-col items-center justify-between bg-white p-6 md:p-8 h-full w-full max-w-xl lg:max-w-7xl mx-auto rounded-lg shadow-md">
        {/* Titel */}
        <h2 className="text-blue_royal mt-3 md:mt-5 text-xl md:text-2xl select-none pointer-events-none">
          Startorte
        </h2>

        {/* Eingabefeld für neue Adresse */}
        <div className="flex flex-col sm:flex-row items-center justify-between w-full gap-2">
          <label className="whitespace-nowrap text-gray-700 font-medium">
            Neue Adresse
          </label>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="flex-1 h-10 border border-gray-400 px-2 rounded-md w-full sm:w-auto"
            placeholder="Strasse, Plz, Ort"
          />
          <button
            onClick={handleAddBase}
            className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 transition"
          >
            <PlusCircle size={24} color={Colors.blue_royal} />
          </button>
        </div>

        {/* Liste der Adressen */}
        <ul className="w-full overflow-auto mt-4 max-h-60 md:max-h-72">
          {baseOptions.map((adresse, id) => (
            <li
              key={id}
              className="flex flex-col sm:flex-row items-center justify-between mb-2 bg-gray-100 p-3 rounded-md min-h-[56px]"
            >
              {/* Adresse */}
              <div className="w-full sm:w-2/5 text-gray-700">
                {adresse.adresse}
                {defaultAddress === adresse && (
                  <span className="text-green-600 font-semibold">
                    {" "}
                    (Standard)
                  </span>
                )}
              </div>

              {/* Standard setzen */}
              <div className="w-full sm:w-2/5 text-center sm:text-left min-h-[36px] flex items-center">
                {adresse !== defaultAddress && (
                  <CustomButton
                    theme="green"
                    onClick={() => handleSetDefaultAddress(adresse)}
                  >
                    Standard setzen
                  </CustomButton>
                )}
              </div>

              {/* Aktionen: Löschen & Auswählen */}
              <div className="flex items-center mt-2 sm:mt-0 min-h-[36px]">
                <button
                  onClick={() => handleRemoveItem(adresse)}
                  className="btn btn-ghost mr-2 hover:text-red-600 transition"
                >
                  <Trash2 size={20} />
                </button>
                <div className="w-[120px] flex justify-center">
                  {[selectedAddress, startOrt].includes(adresse.adresse) ? (
                    <label className="p-1 text-green-600 font-semibold">
                      Ausgewählt
                    </label>
                  ) : (
                    <CustomButton
                      theme="navy"
                      className="w-full"
                      onClick={() => handleRowSelection(id)}
                    >
                      Auswählen
                    </CustomButton>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>

        {/* Speichern & Schließen */}
        <CustomButton
          theme="navy"
          className="mt-5 w-[200px]"
          onClick={() => onClose()}
        >
          Speichern & Schließen
        </CustomButton>
      </div>
    </Modal>
  );
};

export default StandortModal;
